import { COUNTRIES_BY_CODE, CountryCode } from "@redotech/locale/countries";
import {
  USA_PROVINCES_BY_CODE,
  USA_PROVINCE_CODES,
} from "@redotech/locale/provinces";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { CustomerAddressState } from "@redotech/redo-return-ui/flows/return-flow/condition-types/customer-address";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";

export const CustomerAddressDetails = memo(function CustomerAddressDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<CustomerAddressState>) {
  const [searchString, setSearchString] = useState("");

  // Country options - currently only US
  const countryOptions: BadgeRedoListItem<CountryCode>[] = useMemo(() => {
    return [
      {
        id: "US",
        value: "US",
        type: "badge" as const,
        badge: { text: COUNTRIES_BY_CODE.US, color: "gray" as const },
      },
    ];
  }, []);

  const selectedCountry: BadgeRedoListItem<CountryCode> | undefined =
    useMemo(() => {
      if (!state.country) return undefined;
      return {
        id: state.country as CountryCode,
        value: state.country as CountryCode,
        type: "badge" as const,
        badge: {
          text:
            COUNTRIES_BY_CODE[state.country as CountryCode] || state.country,
          color: "gray" as const,
        },
      };
    }, [state.country]);

  // Province options (US only for now)
  const provinceOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (state.country !== "US") return [];
    return USA_PROVINCE_CODES.map((provinceCode) => ({
      id: provinceCode,
      value: provinceCode,
      type: "badge" as const,
      badge: {
        text: USA_PROVINCES_BY_CODE[provinceCode] || provinceCode,
        color: "gray" as const,
      },
    }));
  }, [state.country]);

  const filteredProvinceOptions = useMemo(() => {
    if (!searchString) {
      return provinceOptions;
    }
    const searchLower = searchString.toLowerCase();
    return provinceOptions.filter((option) => {
      const text = option.badge.text;
      const id = option.id;
      return (
        (typeof text === "string" &&
          text.toLowerCase().includes(searchLower)) ||
        (typeof id === "string" && id.toLowerCase().includes(searchLower))
      );
    });
  }, [provinceOptions, searchString]);

  const selectedProvinces: BadgeRedoListItem<string>[] = useMemo(() => {
    return [...state.provinceCodes].map((provinceCode) => ({
      id: provinceCode,
      value: provinceCode,
      type: "badge" as const,
      badge: {
        text: USA_PROVINCES_BY_CODE[provinceCode] || provinceCode,
        color: "gray" as const,
      },
    }));
  }, [state.provinceCodes]);

  return (
    <Flex dir="column" gap="md">
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        label="Country"
        options={countryOptions}
        optionSelected={(item) => {
          setState({ country: item.item.value, provinceCodes: [] });
        }}
        placeholder="Select country..."
        selectedItem={selectedCountry}
        size="xs"
      />
      {state.country === "US" && (
        <RedoTagsDropdownInput
          disabled={disabled}
          label="States/Provinces"
          options={filteredProvinceOptions}
          placeholder="Select states..."
          searchPlaceholder="Search states..."
          searchString={searchString}
          selectedOptions={selectedProvinces}
          setSearchString={setSearchString}
          setSelectedOptions={(items) => {
            setState({
              country: state.country,
              provinceCodes: items.map((item) => item.value),
            });
          }}
          size="xs"
        />
      )}
    </Flex>
  );
});
